import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { toast } from 'react-toastify';
import EventBus from "../common/EventBus";
import InstallKeyService from "../services/installKey.service";

const InstallKeyManager = () => {
    const { user: currentUser } = useSelector((state) => state.auth);
    const [installKeys, setInstallKeys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [validationError, setValidationError] = useState("");

    useEffect(() => {
        if (!currentUser || !currentUser.roles.includes("ROLE_ADMIN")) {
            return;
        }
        fetchInstallKeys();
    }, [currentUser]);

    const fetchInstallKeys = () => {
        setLoading(true);
        InstallKeyService.getAllInstallKeys().then(
            (response) => {
                setInstallKeys(response.data);
                setLoading(false);
            },
            (error) => {
                const _content =
                    (error.response && error.response.data) ||
                    error.message ||
                    error.toString();
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to fetch install keys: " + _content });
                setLoading(false);
            }
        );
    };

    const startEditing = (key) => {
        setEditingId(key.id);
        setEditValue(key.installKey);
        setValidationError("");
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditValue("");
        setValidationError("");
    };

    const saveEdit = (id) => {
        if (!editValue || editValue.trim().length === 0) {
            setValidationError("Install key cannot be empty.");
            return;
        }
        setValidationError("");

        setLoading(true);
        InstallKeyService.updateInstallKey(id, editValue).then(
            (response) => {
                toast.success("Install key updated successfully.");
                setEditingId(null);
                // Refresh the list to get updated createdAt and verify
                fetchInstallKeys();
            },
            (error) => {
                const _content =
                    (error.response && error.response.data) ||
                    error.message ||
                    error.toString();
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to update install key: " + _content });
                setLoading(false);
            }
        );
    };

    if (!currentUser) {
        return <Navigate to="/login" />;
    }

    if (!currentUser.roles.includes("ROLE_ADMIN")) {
        return (
            <div className="container">
                <header className="jumbotron">
                    <h3>
                        <strong>Unauthorized:</strong> You must be an administrator to access this page.
                    </h3>
                </header>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
            <div className="card border-0 shadow-lg rounded-4 overflow-hidden mb-4">
                <div className="card-header bg-white border-0 py-3 px-4 d-flex align-items-center justify-content-between">
                    <h5 className="mb-0 fw-bold text-dark d-flex align-items-center">
                        <i className="bi bi-key-fill text-primary me-2 fs-4"></i>
                        Install Key Management
                    </h5>
                    {loading && (
                        <span className="spinner-border spinner-border-sm text-primary"></span>
                    )}
                </div>
                
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light">
                        <tr>
                                <th scope="col" className="text-secondary fw-semibold py-3 ps-4">ID</th>
                                <th scope="col" className="text-secondary fw-semibold py-3">Install Key</th>
                                <th scope="col" className="text-secondary fw-semibold py-3">Install Date</th>
                                <th scope="col" className="text-secondary fw-semibold py-3">Last Interacted (Created At)</th>
                                <th scope="col" className="text-secondary fw-semibold py-3 pe-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="border-top-0">
                        {installKeys.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="text-center">No install keys found.</td>
                            </tr>
                        ) : (
                            installKeys.map((key) => (
                                <tr key={key.id}>
                                    <td className="ps-4 text-muted small">{key.id}</td>
                                    <td className="fw-medium text-dark">
                                        {editingId === key.id ? (
                                            <div>
                                                <input
                                                    type="text"
                                                    className={`form-control form-control-sm bg-light border-0 ${validationError ? 'is-invalid' : ''}`}
                                                    value={editValue}
                                                    onChange={(e) => { setEditValue(e.target.value); setValidationError(""); }}
                                                    autoFocus
                                                />
                                                {validationError && <div className="invalid-feedback small">{validationError}</div>}
                                            </div>
                                        ) : (
                                            key.installKey
                                        )}
                                    </td>
                                    <td className="text-muted small">{key.installDate}</td>
                                    <td className="text-muted small">{key.createdAt ? new Date(key.createdAt).toLocaleString() : "-"}</td>
                                    <td className="pe-4">
                                        {editingId === key.id ? (
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-sm btn-premium rounded-pill px-3 shadow-sm"
                                                    onClick={() => saveEdit(key.id)}
                                                    disabled={loading}
                                                >
                                                    <i className="bi bi-check2 me-1"></i> Save
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                                                    onClick={cancelEditing}
                                                    disabled={loading}
                                                >
                                                    <i className="bi bi-x me-1"></i> Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                className="btn btn-sm btn-light text-primary fw-medium rounded-pill px-3 shadow-sm"
                                                onClick={() => startEditing(key)}
                                                disabled={loading}
                                            >
                                                <i className="bi bi-pencil-square me-1"></i> Edit
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
        </div>
    </div>
    );
};

export default InstallKeyManager;
